
/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast, Toaster } from 'sonner';
import axios from 'axios';
import {
    CheckCircle, XCircle, Trash2, Loader2, AlertCircle,
    User, Phone, Mail, Building, X, Eye, FileText, Download,
    Filter, Briefcase
} from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_BASE_URL_NEW;

export default function AdminReferrals() {
    const [referrals, setReferrals] = useState([]);
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [updatingId, setUpdatingId] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const [selectedReferral, setSelectedReferral] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);

    // Filters
    const [selectedServiceId, setSelectedServiceId] = useState('');

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
        total: 0,
        per_page: 10
    });

    useEffect(() => {
        const fetchReferrals = async () => {
            const token = localStorage.getItem('admin_token');

            if (!token) {
                setError(
                    <div className="text-center space-y-6">
                        <div className="text-xl font-semibold text-gray-800">
                            You need to be logged in to view this page
                        </div>
                        <button
                            onClick={() => window.location.href = '/admin/auth/login'}
                            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-medium rounded-lg shadow-md transition-all transform hover:scale-105"
                        >
                            <span>Go to Login Page</span>
                        </button>
                    </div>
                );
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                // Fetch referrals with pagination
                const response = await axios.get(`${API_BASE}/admin/referrals`, {
                    params: { per_page: 10, page: currentPage },
                    headers: { Authorization: `Bearer ${token}` },
                });

                // Handle response structure - could be response.data.data or response.data
                const referralsData = response.data.data || response.data;
                setReferrals(Array.isArray(referralsData) ? referralsData : (referralsData?.data || []));

                // Update pagination if available
                if (response.data.current_page !== undefined) {
                    setPagination({
                        current_page: response.data.current_page || 1,
                        last_page: response.data.last_page || 1,
                        total: response.data.total || 0,
                        per_page: response.data.per_page || 10
                    });
                }

                // Fetch services for filter dropdown
                const servicesResponse = await axios.get(`${API_BASE}/admin/services`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setServices(servicesResponse.data.data || []);

                setError(null);
            } catch (err) {
                setError(err.response?.data?.message || 'Failed to load data');
            } finally {
                setLoading(false);
            }
        };

        fetchReferrals();
    }, [currentPage]);

    const handleUpdateStatus = async (id, newStatus) => {
        const token = localStorage.getItem('admin_token');
        setUpdatingId(id);

        try {
            await axios.patch(
                `${API_BASE}/admin/referrals/${id}`,
                { status: newStatus },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setReferrals(prev =>
                prev.map(r => (r.id === id ? { ...r, status: newStatus } : r))
            );

            // Update selected referral if it's the same one
            if (selectedReferral && selectedReferral.id === id) {
                setSelectedReferral({ ...selectedReferral, status: newStatus });
            }

            toast.success(`Referral ${newStatus === 'accepted' ? 'accepted' : 'rejected'} successfully`);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to update status');
        } finally {
            setUpdatingId(null);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this referral?')) return;

        const token = localStorage.getItem('admin_token');
        setDeletingId(id);

        try {
            await axios.delete(`${API_BASE}/admin/referrals/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            setReferrals(prev => prev.filter(r => r.id !== id));

            // Close modal if deleted referral is selected
            if (selectedReferral && selectedReferral.id === id) {
                setSelectedReferral(null);
            }

            toast.success('Referral deleted successfully');

            // Refresh list if current page becomes empty
            if (referrals.length === 1 && currentPage > 1) {
                setCurrentPage(currentPage - 1);
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to delete referral');
        } finally {
            setDeletingId(null);
        }
    };

    const getStatusBadge = (status) => {
        const styles = {
            pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
            accepted: 'bg-green-100 text-green-800 border-green-300',
            rejected: 'bg-red-100 text-red-800 border-red-300',
        };

        return (
            <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium border ${styles[status] || 'bg-gray-100'}`}>
                {status.charAt(0).toUpperCase() + status.slice(1)}
            </span>
        );
    };

    const openDetailsModal = async (referral) => {
        const token = localStorage.getItem('admin_token');
        setLoadingDetails(true);
        setSelectedReferral(referral); // Show basic info immediately

        try {
            // Fetch full details from API
            const response = await axios.get(`${API_BASE}/admin/referrals/${referral.id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            // Update with full details
            setSelectedReferral(response.data.data || response.data);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to load referral details');
            // Keep showing basic info even if details fail
        } finally {
            setLoadingDetails(false);
        }
    };

    const closeModal = () => {
        setSelectedReferral(null);
    };

    const getFileUrl = (path) => {
        if (!path) return null;
        return `${path}`;
    };

    // Filter referrals locally based on selected service
    const filteredReferrals = selectedServiceId
        ? referrals.filter(r => r.service?.id == selectedServiceId)
        : referrals;

    // Reset pagination when filter changes
    useEffect(() => {
        setCurrentPage(1);
    }, [selectedServiceId]);

    if (loading) return <div className="flex justify-center items-center h-64"><Loader2 className="w-12 h-12 animate-spin text-blue-600" /></div>;

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center h-64 bg-gray-50 p-6 rounded-xl border border-dashed border-red-200">
                <AlertCircle className="w-16 h-16 text-red-500 mb-6" />
                <div className="text-gray-800 text-lg">
                    {typeof error === 'string' ? error : error}
                </div>
            </div>
        );
    }

    return (
        <div className="">
            <Toaster position="top-right" richColors closeButton />

            <div className="">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 mb-2">Referrals</h1>
                        <p className="text-gray-600">Manage incoming referral requests</p>
                    </div>

                    {/* Filter Dropdown */}
                    <div className="flex items-center gap-2 bg-white border border-gray-300 rounded-lg px-3 py-2 shadow-sm min-w-[250px]">
                        <Filter className="w-5 h-5 text-gray-400" />
                        <select
                            value={selectedServiceId}
                            onChange={(e) => setSelectedServiceId(e.target.value)}
                            className="bg-transparent border-none outline-none text-gray-700 w-full focus:ring-0"
                        >
                            <option value="" className='text-gray-700 '>Filter by Service</option>
                            {services.map(service => (
                                <option key={service.id} value={service.id}>
                                    {service.title?.en || service.title?.ar || "Untitled Service"}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Table */}
                <div className="bg-white shadow-lg rounded-xl overflow-hidden border border-gray-200">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Patient</th>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Referrer</th>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Requested Service</th>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Date</th>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Status</th>
                                    <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {filteredReferrals.length > 0 ? (
                                    filteredReferrals.map((ref) => (
                                        <tr key={ref.id} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4">
                                                <div className="font-medium text-gray-900">{ref.patient_first_name} {ref.patient_last_name}</div>
                                                <div className="text-sm text-gray-500">DOB: {ref.patient_dob ? new Date(ref.patient_dob).toLocaleDateString() : 'N/A'}</div>
                                            </td>
                                            <td className="px-6 py-4 text-sm">
                                                <div className="font-medium text-gray-900">{ref.referrer_first_name} {ref.referrer_last_name}</div>
                                                <div className="text-gray-500">{ref.referrer_email}</div>
                                            </td>
                                            <td className="px-6 py-4">
                                                {ref.service ? (
                                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-blue-50 text-blue-700 text-sm font-medium">
                                                        <Briefcase className="w-3.5 h-3.5" />
                                                        {ref.service.title?.en || ref.service.title?.ar}
                                                    </span>
                                                ) : (
                                                    <span className="text-gray-400 text-sm italic">General Referral</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-500">
                                                {new Date(ref.created_at).toLocaleString()}
                                            </td>
                                            <td className="px-6 py-4">{getStatusBadge(ref.status)}</td>
                                            <td className="px-6 py-4 text-center">
                                                <div className="flex justify-center gap-2">
                                                    <button
                                                        onClick={() => openDetailsModal(ref)}
                                                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                        title="View Details"
                                                    >
                                                        <Eye className="w-5 h-5" />
                                                    </button>

                                                    {ref.status === 'pending' && (
                                                        <>
                                                            <button
                                                                onClick={() => handleUpdateStatus(ref.id, 'accepted')}
                                                                disabled={updatingId === ref.id}
                                                                className="p-2 text-green-600 hover:bg-green-50 rounded-lg disabled:opacity-50 transition-colors"
                                                                title="Accept"
                                                            >
                                                                <CheckCircle className="w-5 h-5" />
                                                            </button>
                                                            <button
                                                                onClick={() => handleUpdateStatus(ref.id, 'rejected')}
                                                                disabled={updatingId === ref.id}
                                                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50 transition-colors"
                                                                title="Reject"
                                                            >
                                                                <XCircle className="w-5 h-5" />
                                                            </button>
                                                        </>
                                                    )}

                                                    <button
                                                        onClick={() => handleDelete(ref.id)}
                                                        disabled={deletingId === ref.id}
                                                        className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg disabled:opacity-50 transition-colors"
                                                        title="Delete"
                                                    >
                                                        {deletingId === ref.id ? <Loader2 className="w-5 h-5 animate-spin" /> : <Trash2 className="w-5 h-5" />}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                                            <div className="flex flex-col items-center">
                                                <div className="bg-gray-100 p-4 rounded-full mb-3">
                                                    <Filter className="w-8 h-8 text-gray-400" />
                                                </div>
                                                <p className="text-lg font-medium">No referrals found</p>
                                                <p className="text-sm">Try adjusting your filters</p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {pagination.last_page > 1 && (
                        <div className="px-6 py-4 border-t flex justify-between items-center bg-gray-50">
                            <button
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                Previous
                            </button>
                            <span className="text-sm text-gray-600">
                                Page {pagination.current_page} of {pagination.last_page} ({pagination.total} total)
                            </span>
                            <button
                                disabled={currentPage >= pagination.last_page}
                                onClick={() => setCurrentPage(p => Math.min(pagination.last_page, p + 1))}
                                className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                Next
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Custom Details Modal */}
            <AnimatePresence>
                {selectedReferral && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={closeModal}
                            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                        />

                        <motion.div
                            initial={{ scale: 0.95, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.95, opacity: 0, y: 20 }}
                            className="relative bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
                        >
                            <div className="sticky top-0 bg-white border-b px-8 py-5 flex justify-between items-center z-10">
                                <div>
                                    <h2 className="text-2xl font-bold text-gray-900">Referral Details</h2>
                                    <p className="text-sm text-gray-500 mt-1">ID: #{selectedReferral.id}</p>
                                </div>
                                <button
                                    onClick={closeModal}
                                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                                >
                                    <X className="w-6 h-6 text-gray-500" />
                                </button>
                            </div>

                            <div className="p-8 space-y-8">
                                {/* Service Info Banner if exists */}
                                {selectedReferral.service && (
                                    <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 flex items-center gap-4">
                                        <div className="bg-blue-100 p-3 rounded-lg">
                                            <Briefcase className="w-6 h-6 text-blue-600" />
                                        </div>
                                        <div>
                                            <h4 className="text-sm font-semibold text-blue-900 uppercase tracking-wider mb-0.5">Requested Service</h4>
                                            <p className="text-lg font-medium text-blue-800">
                                                {selectedReferral.service.title?.en || selectedReferral.service.title?.ar}
                                            </p>
                                        </div>
                                    </div>
                                )}

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                    {/* Patient Information */}
                                    <div className="space-y-4">
                                        <h3 className="font-semibold text-lg flex items-center gap-2 text-gray-900 border-b pb-2">
                                            <User className="w-5 h-5 text-gray-500" />
                                            Patient Information
                                        </h3>
                                        <div className="space-y-3 pl-1">
                                            <div>
                                                <span className="block text-sm font-medium text-gray-500">Full Name</span>
                                                <span className="text-base text-gray-900">{selectedReferral.patient_first_name} {selectedReferral.patient_last_name}</span>
                                            </div>
                                            <div>
                                                <span className="block text-sm font-medium text-gray-500">Date of Birth</span>
                                                <span className="text-base text-gray-900">{selectedReferral.patient_dob ? new Date(selectedReferral.patient_dob).toLocaleDateString() : 'N/A'}</span>
                                            </div>
                                            <div>
                                                <span className="block text-sm font-medium text-gray-500">Location</span>
                                                <span className="text-base text-gray-900">{selectedReferral.current_location}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Referrer Information */}
                                    <div className="space-y-4">
                                        <h3 className="font-semibold text-lg flex items-center gap-2 text-gray-900 border-b pb-2">
                                            <Building className="w-5 h-5 text-gray-500" />
                                            Referrer Information
                                        </h3>
                                        <div className="space-y-3 pl-1">
                                            <div>
                                                <span className="block text-sm font-medium text-gray-500">Full Name</span>
                                                <span className="text-base text-gray-900">{selectedReferral.referrer_first_name} {selectedReferral.referrer_last_name}</span>
                                            </div>
                                            <div>
                                                <span className="block text-sm font-medium text-gray-500">Email Address</span>
                                                <a href={`mailto:${selectedReferral.referrer_email}`} className="text-base text-blue-600 hover:underline">{selectedReferral.referrer_email}</a>
                                            </div>
                                            <div>
                                                <span className="block text-sm font-medium text-gray-500">Phone Number</span>
                                                <a href={`tel:${selectedReferral.referrer_phone}`} className="text-base text-blue-600 hover:underline">{selectedReferral.referrer_phone}</a>
                                            </div>
                                            <div>
                                                <span className="block text-sm font-medium text-gray-500">Professional Status</span>
                                                <span className={`inline-block px-2 py-0.5 rounded text-sm ${selectedReferral.is_healthcare_professional ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700'}`}>
                                                    {selectedReferral.is_healthcare_professional ? 'Healthcare Professional' : 'Family Member/Other'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Additional Details */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                    <div className="space-y-4">
                                        <h3 className="font-semibold text-lg flex items-center gap-2 text-gray-900 border-b pb-2">
                                            <FileText className="w-5 h-5 text-gray-500" />
                                            Status & Insurance
                                        </h3>
                                        <div className="space-y-3 pl-1">
                                            <div>
                                                <span className="block text-sm font-medium text-gray-500 mb-1">Current Status</span>
                                                {getStatusBadge(selectedReferral.status)}
                                            </div>
                                            <div>
                                                <span className="block text-sm font-medium text-gray-500">Insurance Number</span>
                                                <span className="text-base text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded inline-block">
                                                    {selectedReferral.insurance_number || 'Not Provided'}
                                                </span>
                                            </div>
                                            <div>
                                                <span className="block text-sm font-medium text-gray-500">Submission Date</span>
                                                <span className="text-base text-gray-900">{new Date(selectedReferral.created_at).toLocaleString()}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Insurance Card Preview */}
                                    <div className="space-y-4">
                                        <h3 className="font-semibold text-lg flex items-center gap-2 text-gray-900 border-b pb-2">
                                            <Download className="w-5 h-5 text-gray-500" />
                                            Documents
                                        </h3>
                                        {selectedReferral.insurance_card_url ? (
                                            <div className="space-y-3">
                                                {loadingDetails && (
                                                    <div className="flex items-center justify-center py-4">
                                                        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                                                    </div>
                                                )}
                                                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 flex items-center justify-between">
                                                    <div className="flex items-center gap-3">
                                                        <FileText className="w-8 h-8 text-blue-500" />
                                                        <div>
                                                            <p className="text-sm font-medium text-gray-900">Insurance Card</p>
                                                            <p className="text-xs text-gray-500">Document attached</p>
                                                        </div>
                                                    </div>
                                                    <a
                                                        href={getFileUrl(selectedReferral.insurance_card_url)}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors border border-blue-200"
                                                        title="Download"
                                                    >
                                                        <Download className="w-5 h-5" />
                                                    </a>
                                                </div>

                                                {/* Image Preview */}
                                                {selectedReferral.insurance_card_url && selectedReferral.insurance_card_url.toLowerCase().match(/\.(jpg|jpeg|png)$/i) && (
                                                    <div className="border rounded-lg overflow-hidden bg-gray-50">
                                                        <img
                                                            src={getFileUrl(selectedReferral.insurance_card_url)}
                                                            alt="Insurance Card Preview"
                                                            className="w-full h-48 object-contain bg-white"
                                                            onError={(e) => {
                                                                e.target.parentElement.style.display = 'none';
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="text-sm text-gray-500 italic bg-gray-50 p-4 rounded-lg border border-dashed border-gray-300">
                                                No documents uploaded with this referral.
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="sticky bottom-0 bg-gray-50 border-t px-8 py-5 flex justify-end">
                                <button
                                    onClick={closeModal}
                                    className="px-6 py-2.5 bg-gray-900 text-white font-medium rounded-lg hover:bg-gray-800 transition shadow-lg shadow-gray-200"
                                >
                                    Close Details
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
}
